// Libs
import classNames from 'classnames/bind';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid2, Stack } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseToolbar, Loading, UnderDevelopment } from '~/components/common';
import {
  InfoInventoryForm,
  ProductOptionInventoryForm,
  StatusSummaryInventoryForm,
  IncludeAddOnsInventoryForm,
  ProductVariantInventoryForm,
  ChecklistInventoryForm,
} from '~/components/module/inventory';
// Others
import { IBodyUpdate } from '~/utils/interface/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { TypeDetailInventoryEnum } from '~/utils/enum/common';
import { defaultValuesInventoryForm, schema } from './helper';
import { BACK_TO_PREVIOUS_PAGE, EMPTY_STRING } from '~/utils/constants/common';
import {
  IChecklist,
  IDetailVariant,
  IFormInventory,
  IInventoryDetail,
  IParamDetailInventory,
  IProductAddOn,
} from '~/utils/interface/inventory';
import {
  compressedImage,
  createFormData,
  getChangedDataForm,
  getParseHtmlText,
  removeEmptyObjects,
} from '~/utils/helper/common';
import { createInventory, getDetailInventory, updateInventory } from '~/thunks/inventory/inventoryThunk';
// Styles, images, icons
import styles from './InventoryForm.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const InventoryForm = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/inventory', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: inventoryId } = useParams();

  const {
    control,
    setValue,
    handleSubmit,
    watch,
    reset,
    trigger,
    getValues,
    setError,
    clearErrors,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<IFormInventory>({
    resolver: yupResolver(schema(t, inventoryId)),
    defaultValues: defaultValuesInventoryForm,
  });

  const { isAddOn } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [initialData, setInitialData] = useState<IFormInventory>(defaultValuesInventoryForm);

  const [removeFileIds, setRemoveFileIds] = useState<string[]>([]);
  const [removeAddOnIds, setRemoveAddOnIds] = useState<string[]>([]);
  const [removeChecklistIds, setRemoveChecklistIds] = useState<string[]>([]);
  const [removeVariantIds, setRemoveVariantIds] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  const disable = useMemo(() => {
    return inventoryId ? Object.keys(dirtyFields).length === 0 : false;
  }, [inventoryId, JSON.stringify(dirtyFields)]);
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!inventoryId) return;

    const params: IParamDetailInventory = {
      inventoryId: inventoryId,
      type: TypeDetailInventoryEnum.OVERVIEW,
    };

    handleGetDetailInventory(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailInventory = (params: IParamDetailInventory) => {
    setIsLoading(true);

    dispatch(getDetailInventory(params))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { id, inventoryImages, inventoryFiles, addOns, variants, checklistItems, ...remainingData } =
          res?.data as IInventoryDetail;
        const body: IFormInventory = {
          ...remainingData,
          name: remainingData?.name ?? EMPTY_STRING,
          url: remainingData?.url ?? EMPTY_STRING,
          description: remainingData?.description ?? EMPTY_STRING,
          brand: remainingData?.brand ?? EMPTY_STRING,
          purchasePrice: remainingData?.purchasePrice ?? EMPTY_STRING,
          quantity: remainingData?.quantity ?? EMPTY_STRING,
          replacementCosts: remainingData?.replacementCosts ?? EMPTY_STRING,
          rentalPrice: remainingData?.rentalPrice ?? EMPTY_STRING,
          isAddOn: remainingData?.isAddOn ?? false,
          productId: remainingData?.inventoryProductId ?? EMPTY_STRING,
          productDesignation: remainingData?.productDesignation ?? EMPTY_STRING,
          weight: remainingData?.weight ?? EMPTY_STRING,
          dimensions: remainingData?.dimensions ?? EMPTY_STRING,
          timeCapacity: remainingData?.timeCapacity ?? EMPTY_STRING,
          timeLoading: remainingData?.timeLoading ?? EMPTY_STRING,
          timeUnloading: remainingData?.timeUnloading ?? EMPTY_STRING,
          timeDelivery: remainingData?.timeDelivery ?? EMPTY_STRING,
          timePickup: remainingData?.timePickup ?? EMPTY_STRING,
          isDamageWaiver: remainingData?.isDamageWaiver ?? false,
          isDiscountable: remainingData?.isDiscountable ?? false,
          isTaxable: remainingData?.isTaxable ?? false,
          isShowOnPrint: remainingData?.isShowOnPrint ?? false,
          note: remainingData.note ?? EMPTY_STRING,
          warehouseLocation: remainingData?.warehouseLocation ?? EMPTY_STRING,
          reason: remainingData.reason ?? EMPTY_STRING,
          purchaseDate: remainingData?.purchaseDate ?? EMPTY_STRING,
          purchaseCost: remainingData?.purchaseCost ?? EMPTY_STRING,
          categoryId: remainingData?.category?.id ?? EMPTY_STRING,
          categoryName: remainingData?.category?.name ?? EMPTY_STRING,
          inventoryFile: inventoryFiles.map((item) => {
            return item ? { id: item.id, url: item.url, file: item as any } : {};
          }),
          inventoryImage: inventoryImages.map((item) => {
            return item ? { id: item.id, url: item.url, file: item as any } : {};
          }),
          addOns: addOns?.map((item: IProductAddOn) => {
            return {
              id: String(item?.id || EMPTY_STRING),
              name: item?.name || EMPTY_STRING,
              addOnId: String(item?.addOnId || EMPTY_STRING),
              isRequired: item?.isRequired,
              variantIds: item?.variants?.map((variant) => String(variant?.id || EMPTY_STRING)),
              isShowOnWeb: item?.isShowOnWeb,
              quantity: Number(item?.addOnQuantity || EMPTY_STRING),
              price: String(item?.addOnPrice || EMPTY_STRING),
              productId: item?.addOnId,
              isHasVariant: item?.isHasVariant,
            };
          }),
          checklistItems: checklistItems?.map((item: IChecklist) => {
            return {
              id: String(item?.id || EMPTY_STRING),
              name: String(item?.name || EMPTY_STRING),
              description: String(item?.description || EMPTY_STRING),
            };
          }),
          variants: variants?.map((item: IDetailVariant) => {
            return {
              id: String(item.id || EMPTY_STRING),
              name: String(item.name || EMPTY_STRING),
              length: String(item.length || EMPTY_STRING),
              size: String(item.size || EMPTY_STRING),
              color: String(item.color || EMPTY_STRING),
              height: String(item.height || EMPTY_STRING),
              weight: String(item.weight || EMPTY_STRING),
              style: String(item.style || EMPTY_STRING),
              description: String(item.description || EMPTY_STRING),
              quantity: String(item.quantity || EMPTY_STRING),
              rentalPrice: String(item.rentalPrice || EMPTY_STRING),
              variantImage: item.variantImage ? { id: item.fileId, url: item.variantImage } : {},
            };
          }),
        };

        setInitialData(body);
        reset(body);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleFormInventory = (data: IFormInventory) => {
    if (inventoryId) {
      handleEditInventory(data);
    } else {
      handleAddInventory(data);
    }

    setIsLoading(true);
  };

  const handleAddInventory = async (data: IFormInventory) => {
    if (data.isAddOn) delete data.addOns;

    const { variants, checklistItems } = data;

    const convertData = {
      ...data,
      ...(!data.isAddOn && { addOns: data.addOns?.map(({ id, productId, productValidate, name, ...rest }) => rest) }),
      ...(checklistItems && { checklistItems: data.checklistItems?.map(({ id, ...rest }) => rest) }),
      ...(variants && {
        variants: variants.map(({ id, variantImage, ...rest }) => rest),
      }),
      name: getParseHtmlText(data.name).text,
      url: getParseHtmlText(data.name).url,
    };

    const formData = await createFormData(removeEmptyObjects(convertData), ['inventoryImage', 'inventoryFile']);

    if (variants && variants.length > 0) {
      await Promise.all(
        variants?.map(async (item) => {
          if (item?.variantImage?.file) {
            const image = await compressedImage(item?.variantImage?.file);
            formData.append(`variantImage_${item.uuid}`, image);
          }
        }) || []
      );
    }

    dispatch(createInventory(formData))
      .unwrap()
      .then((_res) => {
        handleCancel();
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditInventory = async (data: IFormInventory) => {
    if (data.isAddOn) delete data.addOns;

    const { variants, checklistItems, inventoryFile, categoryName, ...res } = data;

    const dataChange = getChangedDataForm(dirtyFields, res) as IFormInventory;

    const convertData = {
      ...dataChange,

      ...(dataChange.rentalPrice && {
        rentalPrice: Number(dataChange?.rentalPrice),
      }),

      ...(!dataChange.isAddOn && {
        addOns: dataChange.addOns?.map(({ id, productId, productValidate, name, ...rest }) => {
          const base = { ...rest, price: Number(rest?.price) };
          return id && id.length > 15 ? base : { id, ...base };
        }),
      }),

      ...(checklistItems && {
        checklistItems: checklistItems?.map(({ id, ...rest }) => {
          const base = { ...rest };

          return id && id.length > 15 ? base : { id, ...base };
        }),
      }),

      ...(variants && {
        variants: variants.map(({ id, fileId, variantImage, ...rest }) => {
          const base = {
            ...rest,
            size: Number(rest.size),
            height: Number(rest.height),
            weight: Number(rest.weight),
            length: Number(rest.length),
            rentalPrice: Number(rest?.rentalPrice),
          };
          return id && id.length > 15 ? base : { id, ...base };
        }),
      }),

      ...(inventoryFile && {
        inventoryFile: inventoryFile.filter((item) => !item.id),
      }),

      ...((dataChange.name || dataChange.url) && {
        name: getParseHtmlText(dataChange.name).text,
        ...(getParseHtmlText(dataChange.name).url && {
          url: getParseHtmlText(dataChange.name).url,
        }),
      }),

      ...(removeFileIds.length && { deleteFiles: removeFileIds }),
      ...(removeAddOnIds.length && { deleteAddOns: removeAddOnIds }),
      ...(removeVariantIds.length && { deleteVariants: removeVariantIds }),
      ...(removeChecklistIds.length && { deleteChecklistItems: removeChecklistIds }),
    };

    const formData = await createFormData(convertData, ['inventoryImage', 'inventoryFile']);

    if (variants && variants.length > 0) {
      await Promise.all(
        variants?.map(async (item) => {
          if (item?.variantImage?.file) {
            const image = await compressedImage(item?.variantImage?.file);
            formData.append(`variantImage_${item.uuid}`, image);
          }
        }) || []
      );
    }

    const params: IBodyUpdate<FormData> = {
      id: inventoryId || EMPTY_STRING,
      body: formData,
    };

    dispatch(updateInventory(params))
      .unwrap()
      .then((_res) => {
        handleCancel();
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCancel = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
    reset();
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleRemoveFile = (fileId: string) => {
    setRemoveFileIds((prev) => [...prev, fileId]);
  };

  const handleRemoveAddOn = (productId: string) => {
    if (productId.length > 15) return;

    setRemoveAddOnIds((prev) => [...prev, productId]);
  };

  const handleRemoveVariant = (variantId: string) => {
    if (variantId.length > 15) return;

    setRemoveVariantIds((prev) => [...prev, variantId]);
  };

  const handleRemoveChecklist = (checklistId: string) => {
    if (checklistId.length > 15) return;

    setRemoveChecklistIds((prev) => [...prev, checklistId]);
  };
  //#endregion Handle Function

  return (
    <Box id='inventoryFormPage' className={cx('container')}>
      <BaseToolbar title={t(inventoryId ? 'inventory_form_title_edit' : 'inventory_form_title_add')} isBack />

      <FormProvider
        {...({
          control,
          watch,
          reset,
          trigger,
          setValue,
          getValues,
          setError,
          clearErrors,
          formState: { errors, isDirty, dirtyFields },
        } as UseFormReturn<IFormInventory>)}
      >
        <Stack className={cx('body')}>
          <InfoInventoryForm />

          {/*<FileInventoryForm onRemoveFile={handleRemoveFile} />*/}

          <Grid2 columnSpacing={'26px'} container>
            <Grid2 size={6}>
              <ProductOptionInventoryForm />
            </Grid2>

            <Grid2 size={6}>
              <StatusSummaryInventoryForm />
            </Grid2>
          </Grid2>

          {!isAddOn && <IncludeAddOnsInventoryForm onRemoveAddOn={handleRemoveAddOn} />}

          <ProductVariantInventoryForm onRemoveVariant={handleRemoveVariant} onRemoveFile={handleRemoveFile} />

          <ChecklistInventoryForm onRemoveChecklist={handleRemoveChecklist} />
        </Stack>
      </FormProvider>

      <Stack direction={'row'} gap={3} alignItems={'center'} justifyContent={'flex-end'}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCancel}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button
          variant='contained'
          className={cx('buttonSave')}
          onClick={handleSubmit(handleFormInventory)}
          disabled={disable}
        >
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </Stack>

      {isLoading && <Loading />}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Box>
  );
};

export default InventoryForm;
